import * as React from 'react'
import { connect } from 'react-redux'

import { ratesFilterChanged, trackGoogleAnalyticsEvent } from 'common/actions'

import { AprType, AprTypeOption, LoanRatesFilter } from 'embed/constants'
import { LoanOffer } from 'embed/constants/models'
import { ConfigServiceDataKeys, GoogleAnalyticsActions, LoanPurposeOptions, Product } from 'even-ts-static'
import ToggleFilterList, { ToggleFilterListStyles } from '../ToggleFilterList'

export interface OwnProps {
  styles?: ToggleFilterListStyles
}

export interface StoreProps {
  allLoanOffers?: LoanOffer[]
  aprTypeRatesFilters?: AprType[]
  dispatchRatesFilterChanged?: (filterType: LoanRatesFilter, filterValue: unknown, replace: boolean) => void
  dispatchTrackGoogleAnalyticsRatesFilterChanged?: () => void
  loanPurposeOptions?: LoanPurposeOptions[]
}

type Props = OwnProps & StoreProps

const AprTypeToggleFilter: React.FC<Props> = ({
  aprTypeRatesFilters,
  dispatchRatesFilterChanged,
  dispatchTrackGoogleAnalyticsRatesFilterChanged,
  styles,
  allLoanOffers,
  loanPurposeOptions,
}) => {
  const onToggleFilterClick = React.useCallback(
    (value: AprType) => {
      if (!aprTypeRatesFilters?.includes(value)) {
        dispatchRatesFilterChanged?.(LoanRatesFilter.AprType, value, true)
        dispatchTrackGoogleAnalyticsRatesFilterChanged?.()
      }
    },
    [aprTypeRatesFilters, dispatchRatesFilterChanged, dispatchTrackGoogleAnalyticsRatesFilterChanged],
  )

  const showAprTypeFilter = React.useCallback(
    () =>
      aprTypeRatesFilters?.length &&
      !!loanPurposeOptions?.find((opts) => opts === LoanPurposeOptions.STUDENT_REFINANCE) &&
      !!allLoanOffers?.find((offer) => offer.aprType === AprType.Variable) &&
      !!allLoanOffers?.find((offer) => offer.aprType === AprType.Fixed),
    [aprTypeRatesFilters, loanPurposeOptions, allLoanOffers],
  )

  return showAprTypeFilter() ? (
    <ToggleFilterList
      customStyles={styles}
      filters={[
        {
          active: !!aprTypeRatesFilters?.includes(AprType.Fixed),
          label: AprTypeOption.Fixed,
          value: AprType.Fixed,
        },
        {
          active: !!aprTypeRatesFilters?.includes(AprType.Variable),
          label: AprTypeOption.Variable,
          value: AprType.Variable,
        },
      ]}
      onToggleFilterClick={onToggleFilterClick}
      testId="aprTypeFilter"
    />
  ) : null
}

const mapStateToProps = (state) => ({
  allLoanOffers: state.rates?.[Product.Loan]?.loanOffers ?? [],
  aprTypeRatesFilters: state.rates?.filters?.[Product.Loan]?.[LoanRatesFilter.AprType],
  loanPurposeOptions: state.config?.[ConfigServiceDataKeys.loanPurposeOptions] ?? [],
})

const mapDispatchToProps = (dispatch) => ({
  dispatchRatesFilterChanged: (filterType: LoanRatesFilter, filterValue: unknown, replace: boolean) =>
    dispatch(ratesFilterChanged(Product.Loan, filterType, filterValue, replace, true)),
  dispatchTrackGoogleAnalyticsRatesFilterChanged: () =>
    dispatch(
      trackGoogleAnalyticsEvent(
        GoogleAnalyticsActions.RatesFilterChanged,
        undefined,
        Product.Loan,
        LoanRatesFilter.AprType,
      ),
    ),
})

export default connect<StoreProps, {}, OwnProps>(mapStateToProps, mapDispatchToProps)(AprTypeToggleFilter)
